import { type NodeKey, DecoratorNode, DOMConversionMap, DOMConversionOutput, DOMExportOutput, LexicalNode, SerializedLexicalNode, Spread } from 'lexical';
import { Image, ImageProperties } from '../components/Image';
import { getAttributes, setAttributes } from './utilities';

export function $isImageNode(node: LexicalNode | null | undefined): node is InlineImageNode
{
    return node instanceof InlineImageNode;
}

export type SerializedImageNodeExtension =
{
    properties?: ImageProperties;
    type: 'inline-image';
    version: 1;
}
export type SerializedImageNode = Spread<SerializedImageNodeExtension, SerializedLexicalNode>;

export class InlineImageNode extends DecoratorNode<JSX.Element>
{
    static TYPE = 'inline-image' as const;

    static getType(): string { return InlineImageNode.TYPE; }

    static clone(node: InlineImageNode): InlineImageNode
    {
        return new InlineImageNode(node.properties, node.__key);
    }

    static importJSON(serializedNode: SerializedImageNode): InlineImageNode
    {
        const { properties } = serializedNode;
        const node = $createInlineImageNode(properties ?? { src: '' });
        // const nestedEditor = node.__caption;
        // const editorState = nestedEditor.parseEditorState(caption.editorState);
        // if (!editorState.isEmpty())
        // {
        //     nestedEditor.setEditorState(editorState);
        // }
        return node;
    }

    exportDOM(): DOMExportOutput
    {
        const element = document.createElement('img');
        const props = this.properties ?? { src: '' };
        if (props.src) { element.setAttribute('src', props.src); }
        if (props.alt) { element.setAttribute('alt', props.alt); }
        if (props.title) { element.setAttribute('title', props.title); }
    if (typeof props.width === 'number' && props.width > 0) { element.setAttribute('width', String(props.width)); }
    if (typeof props.height === 'number' && props.height > 0) { element.setAttribute('height', String(props.height)); }
        // Apply any additional raw attributes captured on import
        const exclude = new Set(['src', 'alt', 'title', 'width', 'height']);
        setAttributes(element, props.attrs || {}, exclude);
        return { element };
    }

    static importDOM(): DOMConversionMap | null
    {
        return { img: () => ({ conversion: $convertImageElement, priority: 0 }) };
    }

    properties?: ImageProperties;

    constructor(properties?: ImageProperties, key?: NodeKey)
    {
        super(key);
        this.properties = properties;
    }

    setSize(width: number, height: number)
    {
        const writable = this.getWritable();
        writable.properties = writable.properties || { src: '' };
        writable.properties.width = width;
        writable.properties.height = height;
    }

    createDOM(): HTMLElement
    {
        const span = document.createElement('span');
        span.className = 'resizable';
        return span;
    }

    updateDOM(): boolean
    {
        return false;
    }

    decorate(): JSX.Element
    {
        const element = <Image nodeKey={this.getKey()} properties={this.properties} />;
        return element;
    }

    exportJSON(): SerializedImageNode
    {
        return { type: InlineImageNode.TYPE, version: 1, properties: this.properties };
    }
}

export function $createInlineImageNode(properties: ImageProperties, key?: NodeKey)
{
    return new InlineImageNode(properties, key);
}

export function $convertImageElement(domNode: Node): null | DOMConversionOutput
{
    if (domNode instanceof HTMLImageElement)
    {
        const src = domNode.getAttribute('src') || '';
        const widthAttr = domNode.getAttribute('width');
        const heightAttr = domNode.getAttribute('height');
        // eslint-disable-next-line no-undefined
        const width = widthAttr != null ? parseInt(widthAttr, 10) : undefined;
        // eslint-disable-next-line no-undefined
        const height = heightAttr != null ? parseInt(heightAttr, 10) : undefined;
        // eslint-disable-next-line no-undefined
        const alt = domNode.getAttribute('alt') || undefined;
        // eslint-disable-next-line no-undefined
        const title = domNode.getAttribute('title') || undefined;
        const attrs: Record<string, string> = {};
        getAttributes(domNode, attrs);
        // eslint-disable-next-line no-undefined
        const node = $createInlineImageNode({ src, width: (width && width > 0) ? width : undefined, height: (height && height > 0) ? height : undefined, alt, title, attrs });
        return { node };
    }
    return null;
}